// frontend/src/contexts/AssistantRailContext.tsx
/**
 * ⭐ HIS ANSWER, 2026-09-30: the chat must be usable BESIDE an open letter. What an open letter needs to know about the
 *   assistant rail, and how it asks for the chat. Provided by `Layout`, which owns the rail - so every letter a page
 *   renders inside `<Layout>` (and the rail's own recordless host) reads it; `null` outside one (a dialog rendered
 *   alone, as its unit tests do).
 *
 * Measured the 112th (13368 Main St): at 1280 the letter's full-screen z-[1200] overlay covered the docked rail and
 *   the top bar's opener; at 375 the only opener was covered, and a letter the CHAT opened landed hidden UNDER its
 *   sheet (same z-index, and the rail comes after the page in the DOM).
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * The docked rail's width (AiRail's frame), and the offset that keeps an open letter's overlay OFF it. ⛔ ONE number,
 * written twice only because Tailwind reads whole class names: an overlay that stopped short of the rail would cover its
 * edge, and one that stopped past it would leave the page showing - and clickable - beside the letter.
 */
export const DOCKED_RAIL_WIDTH = 'xl:w-[342px]';
/**
 * ⭐ The 112th's review (M3): plus the gap the letter's scroll lock pads the page by, where scrollbars take room (a Mac
 * with a mouse, Windows) - the page, and the docked rail with it, moves in by that gap.
 */
export const BESIDE_DOCKED_RAIL = 'xl:right-[calc(342px+var(--scroll-lock-gap,0px))]';

/** While the rail OVERLAYS an open letter (below 1280, and on the map page at any width): which of the two is in front. */
export type RailFront = 'letter' | 'chat';

/** The letter on screen: none, one he is still WRITING, or one Generate FINISHED (its change door is closed). */
export type LetterOnScreen = 'none' | 'writing' | 'finished';

/**
 * ⭐ The 112th's review (I1): with the chat usable beside the letter, a change asked of a FINISHED letter got "No LOI is
 *   open" beside a letter plainly open - and following that advice ("ask me to make one") did nothing, because the open
 *   dialog kept its finished screen. What the chat says instead, true where he acts.
 */
export const FINISHED_LETTER_REVISE =
  'The letter on screen is already generated, so it cannot take a change. Press Done on it, then ask me to make a new LOI and say what to change.';
export const FINISHED_LETTER_OPEN = 'The letter on screen is already generated - press Done on it, then ask me again for a new one.';

/**
 * ⭐ The 112th's review (I2): the rail's History and switcher now sit beside an open letter, and leaving the page unmounts
 *   it - his fields, his accepted drafts (each a paid one), the OM read. Asked first, only while it is still being written.
 */
export const LEAVE_LETTER_QUESTION = 'Leave the letter you are writing? It has not been generated, and what you entered will be lost.';

/** True when the rail may take him off the page: no letter being written, or he said yes. ONE rule for every rail link. */
export const mayLeaveTheLetter = (rail: AssistantRail | null): boolean =>
  rail?.letterOnScreen !== 'writing' || window.confirm(LEAVE_LETTER_QUESTION);

export interface AssistantRail {
  /** The rail is open - docked beside the page at 1280+, over it below (and on the map page at any width). */
  open: boolean;
  /** The map page: the rail overlays at every width and never docks. */
  fullBleed: boolean;
  front: RailFront;
  /** Bring the chat forward - open the rail if it is closed, on its Assistant tab, in front of the letter. */
  showChat: () => void;
  /** Put the letter back in front. The chat stays open behind it, its conversation kept. */
  showLetter: () => void;
  /** A letter opened, and it comes to the front. Returns the call for when it closes. */
  letterOpened: () => () => void;
  letterOnScreen: LetterOnScreen;
  /** The open letter was generated. Returns the call for when it is not on screen any more. */
  letterFinished: () => () => void;
}

export const AssistantRailContext = createContext<AssistantRail | null>(null);

export const useAssistantRail = (): AssistantRail | null => useContext(AssistantRailContext);

/**
 * ⭐ THE RAIL'S SIDE OF AN OPEN LETTER, owned where the rail's open state is (Layout) - and the ONE implementation, which a
 *   test mounts too rather than a copy: how many letters are open, which of a letter and an OVERLAYING rail is in front,
 *   and a count that moves each time a letter asks for the chat (the rail answers with its Assistant tab). A letter that
 *   opens comes to the front; neither ever closes the other - the chat unmounting would lose his conversation.
 */
export function useAssistantRailState({ open, setOpen, fullBleed }: { open: boolean; setOpen: (next: boolean) => void; fullBleed: boolean }) {
  const [lettersOpen, setLettersOpen] = useState(0);
  const [lettersFinished, setLettersFinished] = useState(0);
  const [front, setFront] = useState<RailFront>('letter');
  const [assistantAsked, setAssistantAsked] = useState(0);
  const showChat = useCallback(() => {
    setOpen(true);
    setFront('chat');
    setAssistantAsked((n) => n + 1);
  }, [setOpen]);
  const showLetter = useCallback(() => setFront('letter'), []);
  const letterOpened = useCallback(() => {
    setLettersOpen((n) => n + 1);
    setFront('letter');
    return () => setLettersOpen((n) => n - 1);
  }, []);
  const letterFinished = useCallback(() => {
    setLettersFinished((n) => n + 1);
    return () => setLettersFinished((n) => n - 1);
  }, []);
  const letterOnScreen: LetterOnScreen = lettersOpen === 0 ? 'none' : lettersOpen > lettersFinished ? 'writing' : 'finished';
  const rail = useMemo<AssistantRail>(
    () => ({ open, fullBleed, front, showChat, showLetter, letterOpened, letterOnScreen, letterFinished }),
    [open, fullBleed, front, showChat, showLetter, letterOpened, letterOnScreen, letterFinished],
  );
  const railBesideLetter = { letterOpen: lettersOpen > 0, front, onShowLetter: showLetter, assistantAsked };
  return { rail, railBesideLetter };
}
